import React, { useState } from 'react';
import { enquiriesAPI } from '../../utils/api';
import { 
  INTEREST_OPTIONS, 
  LEVEL_OPTIONS, 
  AGE_GROUP_OPTIONS, 
  TIME_SLOT_OPTIONS, 
  SOURCE_OPTIONS 
} from './constants';

function t(field, lang = 'en') {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field[lang] !== undefined ? field[lang] : (field.en || '');
}

export default function EnquiryForm({ lang = 'en' }) {
  const [form, setForm] = useState({
    name: '',
    mobile: '+852 ',
    email: '',
    message: '',
    interest: '',
    level: 'Beginner',
    ageGroup: '',
    preferredTrialDate: '',
    preferredTimeSlot: '',
    source: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;

    if (!form.name || form.name.trim().length < 2) {
      setStatus({ type: 'error', msg: '⚠️ Please enter your valid Full Name.' });
      return;
    }

    // Validate WhatsApp Number
    const digitsOnly = form.mobile.replace(/\D/g, '');
    const isHK = form.mobile.includes('852') || digitsOnly.startsWith('852');
    if (isHK && digitsOnly.length !== 11 && digitsOnly.length !== 8) {
      setStatus({ 
        type: 'error', 
        msg: '⚠️ Please enter a valid 8-digit Hong Kong WhatsApp number (e.g. +852 6123 4567).' 
      });
      return;
    }
    if (!isHK && (digitsOnly.length < 8 || digitsOnly.length > 15)) {
      setStatus({ 
        type: 'error', 
        msg: '⚠️ Please enter a valid WhatsApp contact number (8 to 15 digits).' 
      });
      return;
    }

    setLoading(true);
    setStatus(null);
    try {
      await enquiriesAPI.create(form);
      setStatus({ type: 'success', msg: t({en:"🎉 Thanks! We'll reach out within 24 hours.", zh:"🎉 謝謝！我們將在 24 小時內與您聯繫。"}, lang) });
      setForm({
        name: '', mobile: '+852 ', email: '', message: '', interest: '', 
        level: 'Beginner', ageGroup: '', preferredTrialDate: '', 
        preferredTimeSlot: '', source: ''
      });
    } catch (err) {
      setStatus({ type: 'error', msg: err.response?.data?.message || 'Something went wrong.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      // Prevent entering alphabets, only allow +, numbers, and spaces
      let cleaned = value.replace(/[^0-9+ ]/g, '');
      const digits = cleaned.replace(/\D/g, '');
      if ((cleaned.includes('852') || digits.startsWith('852')) && digits.length > 11) {
        return; // do not allow typing more than 8 digits after 852
      }
      if (!cleaned.includes('852') && !digits.startsWith('852') && digits.length > 15) {
        return; // max international length
      }
      setForm(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const translateOpt = (opt) => {
    const dict = {
      'Hip Hop': {zh:'嘻哈'}, 'Ballet': {zh:'芭蕾'}, 'Salsa': {zh:'薩爾薩舞'}, 'Contemporary': {zh:'當代舞'}, 'Kathak': {zh:'卡塔克舞'}, 'Breakdance': {zh:'霹靂舞'}, 'Multiple Styles': {zh:'多種風格'}, 'Academy Plan': {zh:'學院方案'},
      'Beginner': {zh:'初級'}, 'Intermediate': {zh:'中級'}, 'Advanced': {zh:'高級'}, 'Professional': {zh:'專業級'},
      'Kids (4-12)': {zh:'兒童 (4-12)'}, 'Teens (13-19)': {zh:'青少年 (13-19)'}, 'Adults (20+)': {zh:'成人 (20+)'}, 'Seniors': {zh:'長者'},
      'Morning': {zh:'早上'}, 'Afternoon': {zh:'下午'}, 'Evening': {zh:'晚上'}, 'Weekend Only': {zh:'僅限週末'},
      'Instagram': {zh:'Instagram'}, 'Facebook': {zh:'Facebook'}, 'Friend Referral': {zh:'朋友介紹'}, 'Google Search': {zh:'Google 搜尋'}, 'Other': {zh:'其他'}
    };
    return dict[opt]?.[lang] || opt;
  };

  return (
    <div className="enquiry-form-container">
      {status && (
        <div className={`form-status-box ${status.type}`} style={{ 
          padding: '12px', borderRadius: '4px', marginBottom: '20px',
          background: status.type === 'success' ? '#e6fffa' : '#fff5f5',
          color: status.type === 'success' ? '#2c7a7b' : '#c53030',
          border: `1px solid ${status.type === 'success' ? '#81e6d9' : '#feb2b2'}`
        }}>
          {status.msg}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-row-2">
          <div className="form-group">
            <label className="form-label-style">{t({en:'Full Name',zh:'全名'}, lang)}</label>
            <input 
              className="form-input-style" 
              name="name"
              placeholder={t({en:'Alex Doe',zh:'王小明'}, lang)} 
              value={form.name} 
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label-style">{t({en:'WhatsApp Number',zh:'WhatsApp 號碼'}, lang)}</label>
            <input 
              className="form-input-style" 
              name="mobile"
              type="tel" 
              placeholder="+852 6681 5305" 
              value={form.mobile} 
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-row-2">
          <div className="form-group">
            <label className="form-label-style">{t({en:'Email Address',zh:'電子郵件'}, lang)}</label>
            <input 
              className="form-input-style" 
              name="email"
              type="email" 
              placeholder="alex@example.com" 
              value={form.email} 
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label-style">{t({en:'Age Group',zh:'年齡層'}, lang)}</label>
            <select className="form-input-style" name="ageGroup" value={form.ageGroup} onChange={handleChange}>
              <option value="">{t({en:'Select Age Group',zh:'選擇年齡層'}, lang)}</option>
              {AGE_GROUP_OPTIONS.map(opt => <option key={opt} value={opt}>{translateOpt(opt)}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row-2">
          <div className="form-group">
            <label className="form-label-style">{t({en:'Interested In',zh:'有興趣的風格'}, lang)}</label>
            <select className="form-input-style" name="interest" value={form.interest} onChange={handleChange}>
              <option value="">{t({en:'Select Style',zh:'選擇風格'}, lang)}</option>
              {INTEREST_OPTIONS.map(opt => <option key={opt} value={opt}>{translateOpt(opt)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label-style">{t({en:'Current Level',zh:'目前水平'}, lang)}</label>
            <select className="form-input-style" name="level" value={form.level} onChange={handleChange}>
              {LEVEL_OPTIONS.map(opt => <option key={opt} value={opt}>{translateOpt(opt)}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row-2">
          <div className="form-group">
            <label className="form-label-style">{t({en:'Preferred Trial Date',zh:'偏好試堂日期'}, lang)}</label>
            <input 
              className="form-input-style" 
              name="preferredTrialDate"
              type="date" 
              value={form.preferredTrialDate} 
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label className="form-label-style">{t({en:'Preferred Time',zh:'偏好時間'}, lang)}</label>
            <select className="form-input-style" name="preferredTimeSlot" value={form.preferredTimeSlot} onChange={handleChange}>
              <option value="">{t({en:'Select Time Slot',zh:'選擇時段'}, lang)}</option>
              {TIME_SLOT_OPTIONS.map(opt => <option key={opt} value={opt}>{translateOpt(opt)}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label-style">{t({en:'How did you hear about us?',zh:'您是從哪裡得知我們的？'}, lang)}</label>
          <select className="form-input-style" name="source" value={form.source} onChange={handleChange}>
            <option value="">{t({en:'Select Source',zh:'選擇來源'}, lang)}</option>
            {SOURCE_OPTIONS.map(opt => <option key={opt} value={opt}>{translateOpt(opt)}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label-style">{t({en:'Your Message *',zh:'您的留言 *'}, lang)}</label>
          <textarea 
            className="form-input-style" 
            name="message"
            placeholder={t({en:'Tell us about your dance goals...',zh:'告訴我們您的舞蹈目標...'}, lang)} 
            required 
            value={form.message} 
            onChange={handleChange}
          />
        </div>

        <button 
          type="submit" 
          className="btn btn-primary btn-lg" 
          style={{ width: '100%', justifyContent: 'center' }} 
          disabled={loading || !form.message.trim()}
        >
          {loading ? t({en:'⏳ Sending...',zh:'⏳ 傳送中...'}, lang) : t({en:'Send Enquiry →',zh:'發送查詢 →'}, lang)}
        </button>
      </form>
    </div>
  );
}
